import { Box, CircularProgress, Chip, Stack } from "@mui/material";
import Select from "react-select";
import { Button, Modal, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import { useState, useRef, useMemo, useEffect } from "react";
import { DatePickerWithValidation, formatYYYYMMDD, dateFieldConfig } from "../../../../utils/DateValidator";

import {
  fetchColumnFilterOptions,
  batchUpdateAppliedFilters,
  setShowFilterModal,
  initialOutstandingFilters,
} from "../redux/ReportsSlice";

import { multiSelectStyles } from "../multiSelectStyle";
import { getColumnFilterEndpoints } from "../optionsConfig";
import closeIcon from "../../../../common/assets/images/x-close.png";
import { normalizeToArray } from "../hooks/useReport";

const REPORT_PATH = "/outstanding-report";

const HIDDEN_FROM_CHIPS = [
  "los",
  "los_op",
  "sort_by",
  "sort_order",
  "queue_type",
];

const FILTER_LABELS = {
  episode_identifier: "Episode ID",
  patient_id: "Patient ID",
  episode_type: "Episode Type",
  admission_date_from: "Admission From",
  admission_date_to: "Admission To",
  discharge_date_from: "Discharge From",
  discharge_date_to: "Discharge To",
  speciality: "Speciality",
  priority: "Priority",
  days_of_outstanding: "Days Outstanding",
  assign_coder: "Assigned Coder",
  current_queue: "Current Queue",
  status: "Status",
};


const filterFields = [
  { label: "Episode ID", name: "episode_identifier", type: "list" },
  { label: "Patient ID", name: "patient_id", type: "list" },
  { label: "Admission Date From", name: "admission_date_from", type: "date" },
  { label: "Admission Date To", name: "admission_date_to", type: "date" },
  { label: "Discharge Date From", name: "discharge_date_from", type: "date" },
  { label: "Discharge Date To", name: "discharge_date_to", type: "date" },
  { label: "Speciality", name: "speciality", type: "list" },
  { label: "Priority Level", name: "priority", type: "list" },
  { label: "Days Outstanding", name: "days_of_outstanding", type: "list" },
  { label: "Assigned Coder", name: "assign_coder", type: "list" },
  { label: "Current Queue", name: "current_queue", type: "list" },
  { label: "Status", name: "status", type: "list" },
];

const OutstandingReportFilter = ({ onFiltersApplied }) => {
    // const isFullScreen = useSelector((s) => s.reports.isTableFullScreen);
    // console.log(isFullScreen)
  const dispatch = useDispatch();
  const debounceRef = useRef({});

  const [searchTexts, setSearchTexts] = useState({});
  const [local, setLocal] = useState({});

  const showFilterModal = useSelector((s) => s.reports.showFilterModal);

  const savedFilters = useSelector(
    (s) => s.reports.appliedFilters[REPORT_PATH] || {},
  );

  const columnOptions = useSelector(
    (s) => s.reports.columnFilterOptions[REPORT_PATH] || {},
  );

  const columnConfig = getColumnFilterEndpoints(REPORT_PATH);

  /* ───────── Chips Logic ───────── */

  const hasAny = useMemo(
    () =>
      Object.entries(local).some(([k, v]) => {
        if (HIDDEN_FROM_CHIPS.includes(k)) return false;
        return Array.isArray(v) ? v.length > 0 : Boolean(v);
      }),
    [local],
  );

   const chips = useMemo(
  () =>
    Object.entries(local).flatMap(([k, v]) => {
      if (HIDDEN_FROM_CHIPS.includes(k) || !v) return [];

      // Status chips
      if (k === "status" && Array.isArray(v)) {
        const statusData = columnOptions?.status?.data || [];

        return v.map((id) => {
          const matched = statusData.find(
            (item) => item.status_id === id
          );

          return {
            key: k,
            value: matched?.status || id,
            actualValue: id,
          };
        });
      }

      // Current Queue chips
      if (k === "current_queue" && Array.isArray(v)) {
        const queueData = columnOptions?.current_queue?.data || [];

        return v.map((id) => {
          const matched = queueData.find(
            (item) => item.queue_id === id
          );

          return {
            key: k,
            value: matched?.queue_name || id,
            actualValue: id,
          };
        });
      }

      // Normal array chips
      if (Array.isArray(v)) {
        return v.map((val) => ({
          key: k,
          value: val,
          actualValue: val,
        }));
      }

      // Single value chips
      return [
        {
          key: k,
          value: v,
          actualValue: v,
        },
      ];
    }),
  [local, columnOptions],
);

 const removeChip = ({ key, actualValue }) => {
  setLocal((prev) => {
    const current = prev[key];

    if (Array.isArray(current)) {
      return {
        ...prev,
        [key]: current.filter((v) => v !== actualValue),
      };
    }

    return {
      ...prev,
      [key]: null,
    };
  });
};

  /* ───────── Helpers ───────── */
   const mapToSelectOptions = (arr,fieldName) =>
    Array.isArray(arr)
      ? arr.map((i) => {
          if (typeof i === "object" && i !== null) {
             // Status
          if (fieldName === "status") {
            return {
              label: i.status,
              value: i.status_id,
            };
          }
           // Queue
          if (fieldName === "current_queue") {
            return {
              label: i.queue_name,
              value: i.queue_id,
            };
          }
          if (fieldName === "assign_coder") {
  return {
    label: i.user_name,
    value: i.user_id,
  };
}
            return {
              label:
                i.user_name ?? i.speciality ??i.queue_name ?? i.label ?? JSON.stringify(i),
              value:
                i.user_name ??
                i.queue_name ??
                i.value ??
                i.user_name ??
                i.queue_name ??
                i.speciality
            };
          }
          return { label: i, value: i };
        })
      : [];
  const getOptionsWithLoader = (fieldName) => {
    const columnState = columnOptions?.[fieldName];
    // const columnData = columnState?.data || [];
    let columnData = normalizeToArray(columnState?.data)
  //     let columnData = columnState?.data || [];

  // if (!Array.isArray(columnData) && typeof columnData === "object") {
  //   const firstArray = Object.values(columnData).find((v) =>
  //     Array.isArray(v)
  //   );

  //   if (firstArray) {
  //     columnData = firstArray;
  //   }
  // }
    const loading = columnState?.loading;

    const baseOptions = mapToSelectOptions(columnData,fieldName);

    if (loading) {
      return [
        ...baseOptions,
        {
          value: "loading",
          label: (
            <div
              style={{ display: "flex", justifyContent: "center", padding: 8 }}>
              <CircularProgress size={18} />
            </div>
          ),
          isDisabled: true,
        },
      ];
    }

    return baseOptions;
  };

const dispatchFetch = (fieldName, search = "", page = 1) => {
  const config = columnConfig[fieldName];
  if (!config) return;

  dispatch(
    fetchColumnFilterOptions({
      reportPath: REPORT_PATH,
      columnKey: fieldName,
      endpoint: config.endpoint,
      responseKey: config.responseKey,
      search,
      page,
      limit: 10,
    }),
  );
};

  const handleInputChange = (fieldName, inputValue, { action } = {}) => {
   if (action === "input-change"){
      setSearchTexts("")
    };

    setSearchTexts((prev) => ({ ...prev, [fieldName]: inputValue }));

    if (debounceRef.current[fieldName])
      clearTimeout(debounceRef.current[fieldName]);

    debounceRef.current[fieldName] = setTimeout(() => {
      dispatchFetch(fieldName, inputValue, 1);
    }, 500);
  };

  const handleMenuOpen = (fieldName) => {
    const columnState = columnOptions?.[fieldName];

    if (!columnState) {
      dispatchFetch(fieldName, "", 1);
      return;
    }

    if (columnState.loading) return;
    if (columnState.hasMore === false) return;
    const data = normalizeToArray(columnState.data)
    const count = data.length

    if (count < 5) {
      dispatchFetch(
        fieldName,
        searchTexts[fieldName] || "",
        (columnState.page ?? 1) + 1
      );
    }
  };

useEffect(() => {
  Object.keys(local).forEach((fieldName) => {
    const columnState = columnOptions?.[fieldName];
    if (!columnState) return;

    const columnData = normalizeToArray(columnState.data);
    const selectedValues = local[fieldName] || [];

    const remaining = columnData.length - selectedValues.length;

    if (
      remaining < 5 &&
      !columnState.loading &&
      columnState.hasMore !== false
    ) {
      dispatchFetch(
        fieldName,
        searchTexts[fieldName] || "",
        (columnState.page ?? 1) + 1
      );
    }
  });
}, [local, columnOptions, searchTexts]);

  const handleScrollBottom = (fieldName) => {
    const columnState = columnOptions?.[fieldName];
    console.log(
    fieldName,
    "page:",
    columnState?.page,
    "hasMore:",
    columnState?.hasMore
  );

    if (!columnState) return;
    if (columnState.loading) return;
    if (columnState.hasMore === false) return;
    console.log(
    fieldName,
    "page:",
    columnState?.page,
    "hasMore:",
    columnState?.hasMore
  );
    
    dispatchFetch(
      fieldName,
      searchTexts[fieldName] || "",
      (columnState.page ?? 1) + 1,
    );
  };

//   const dateFieldConfig = {
//   admission_date_from: {
//     maxDate: new Date(),
//     resetField: "admission_date_to",
//   },

//   admission_date_to: {
//     dependsOn: "admission_date_from",
//     minDateField: "admission_date_from",
//     maxDate: new Date(),
//   },

//   discharge_date_from: {
//     maxDate: new Date(),
//     minDateField: "admission_date_from",
//     resetField: "discharge_date_to",
//   },

//   discharge_date_to: {
//     dependsOn: "discharge_date_from",
//     minDateField: "discharge_date_from",
//     maxDate: new Date(),
//   },
// };


/* ───────── State sync ───────── */

  useEffect(() => {
    if (showFilterModal) {
      setLocal({ ...savedFilters });
    }
  }, [showFilterModal, savedFilters]);

  /* ───────── Apply ───────── */

  const handleApply = () => {
    dispatch(
      batchUpdateAppliedFilters({
        reportPath: REPORT_PATH,
        filters: local,
      }),
    );

    onFiltersApplied?.();

    dispatch(setShowFilterModal(false));
  };

  /* ───────── UI ───────── */

  return (
    <Modal
      show={showFilterModal}
      onHide={() => dispatch(setShowFilterModal(false))}
      dialogClassName="modal-xl"
      style={{zIndex:"15000"}}>
      <Modal.Header
        closeButton
        className="episode-list-modal-header-outstanding-report">
        <Modal.Title className="episode-list-filter-modal-title-outstanding-report">
          Outstanding Report Filters
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Box>
          {hasAny && (
            <Row className="mb-2">
              <Col className="d-flex flex-row justify-content-between align-items-start">
                <Stack direction="row" flexWrap="wrap" sx={{
                flex: 1,
                maxHeight: "80px",
                overflowY: "auto",
                overflowX: "hidden",
                alignContent: "flex-start", }}>
                  {chips.map((c, i) => (
                    <Chip
                      key={i}
                      className="m-1 bg-light"
                      size="small"
                      label={`${FILTER_LABELS[c.key] || c.key} : ${c.value}`}
                      style={{ border: "1px solid #555", fontSize: "12px" }}
                      deleteIcon={
                        <img
                          src={closeIcon}
                          alt="remove"
                          className="episodeListFilterObj-close-icon-him"
                        />
                      }
                      onDelete={() => removeChip(c)}
                    />
                  ))}
                </Stack>

                <Button
                  className="episodelist-reset-btn flex-shrink-0 ms-2"
                  onClick={() => setLocal({ ...initialOutstandingFilters })}>
                  Clear All
                </Button>
              </Col>

              <hr className="mt-3" />
            </Row>
          )}

          <Row className="g-3">
            {filterFields.map((field) => (
              <Col xs={12} sm={6} md={4} key={field.name} className = "filter-model-input-option-field">
                <label className="workflow-outstanding-report-filter-label">
                  {field.label}
                </label>

                {/* {field.type === "date" && (
                  <DatePicker
                    selected={
                      local[field.name] ? new Date(local[field.name]) : null
                    }
                    onChange={(date) =>
                      setLocal((prev) => ({
                        ...prev,
                        [field.name]: date
                          ? date.toISOString().split("T")[0]
                          : null,
                      }))
                    }
                    className="workflow-him-filter-date-picker"
                    dateFormat="dd-MM-yyyy"
                    placeholderText={field.label}
                    isClearable
                  />
                )} */}

                {DatePickerWithValidation(field, local, setLocal)}
                {field.type === "list" && (
                  <Select
                    isMulti
                    className="filter-model-input-option-field"
                    styles={multiSelectStyles}
                    options={getOptionsWithLoader(field.name)}
                    // value={
                    //   Array.isArray(local[field.name])
                    //     ? mapToSelectOptions(local[field.name])
                    //     : []
                    // }
                     value={
                          Array.isArray(local[field.name])
                         ? ["status", "current_queue","assign_coder"].includes(field.name)
                       ? getOptionsWithLoader(field.name).filter((option) =>
                       local[field.name].includes(option.value)
                      )
                     : mapToSelectOptions(local[field.name], field.name)
                     : []
                       }
                    closeMenuOnSelect={false}
                    placeholder={`Select ${field.label}`}
                    isOptionDisabled={(opt) => opt.value === "loading"}
                    onMenuOpen={() => handleMenuOpen(field.name)}
                    onMenuScrollToBottom={() => handleScrollBottom(field.name)}
                    onInputChange={(inputValue, actionMeta) =>
                      handleInputChange(field.name, inputValue, actionMeta)
                    }
                    onChange={(selectedOptions) =>
                      setLocal((prev) => ({
                        ...prev,
                        [field.name]: selectedOptions
                          ? selectedOptions.map((opt) => opt.value)
                          : [],
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                    }}
                  />
                )}

                {field.type === "text" && (
                  <input
                    type="text"
                    // classNamePrefix="react-select"
                    className="form-control"
                    value={local[field.name] || ""}
                    onChange={(e) =>
                      setLocal((prev) => ({
                        ...prev,
                        [field.name]: e.target.value,
                      }))
                    }
                    placeholder={field.label}
                  />
                )}
              </Col>
            ))}
          </Row>
        </Box>
      </Modal.Body>

      <Modal.Footer className="episode-list-filter-modal-footer">
        <Button
          className="el-filter-cancel btn btn-secondary"
          onClick={() => dispatch(setShowFilterModal(false))}>
          Cancel
        </Button>

        <Button className="el-filter-submit" onClick={handleApply}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OutstandingReportFilter;
